import React, { useEffect, useMemo, useState } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DayPost } from '../../types/post';
import { getDayPost, generateDateID } from '../../services/dayPostService';
import { terraDailyService, TerraDailyDoc } from '../../services/terraDailyService';
import Header from '../../components/layout/Header';

const toDateId = (dateStr: string) => dateStr.replace(/-/g, '');

const DailyInsights: React.FC = () => {
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get('date'); // YYYYMMDD
  const dateID = dateParam && /^\d{8}$/.test(dateParam) ? dateParam : (() => {
    const d = new Date();
    return `${d.getFullYear()}${(d.getMonth()+1).toString().padStart(2,'0')}${d.getDate().toString().padStart(2,'0')}`;
  })();
  console.log('[DailyInsights] dateID', dateID);

  const [dayPostData, setDayPostData] = useState<DayPost | null>(null);
  const [dailyDocs, setDailyDocs] = useState<TerraDailyDoc[]>([]);

  // Resolve target date (like other insights pages)
  const targetDate = useMemo(() => {
    if (dateParam && /^\d{8}$/.test(dateParam)) {
      const year = parseInt(dateParam.substring(0, 4));
      const month = parseInt(dateParam.substring(4, 6)) - 1;
      const day = parseInt(dateParam.substring(6, 8));
      return new Date(year, month, day);
    }
    return new Date();
  }, [dateParam]);

  // Fetch DayPost like SleepInsights
  useEffect(() => {
    (async () => {
      if (!currentUser?.uid) return;
      const id = generateDateID(targetDate);
      try {
        const dp = await getDayPost(currentUser.uid, id);
        setDayPostData(dp);
      } catch (e) {
        console.warn('[DailyInsights] failed to load day post', id, e);
        setDayPostData(null);
      }
    })();
  }, [currentUser?.uid, targetDate]);

  const wearableDayData = dayPostData?.wearableDayData || [];
  // Use a stable key to prevent infinite re-fetch loops due to array identity changes
  const wearableKey = useMemo(() => {
    try {
      return JSON.stringify(wearableDayData);
    } catch {
      return '';
    }
  }, [wearableDayData]);

  useEffect(() => {
    (async () => {
      try {
        console.log('[DailyInsights] wearableDayData', wearableDayData);
        const paths: string[] = wearableDayData;
        if (!paths || paths.length === 0) {
          setDailyDocs([]);
          return;
        }
        console.log('[DailyInsights] wearableDayData paths', paths);
        const docs = await terraDailyService.getDailyForDay(paths);
        console.log('[DailyInsights] fetched daily docs count', docs.length, docs.map(d => Object.keys(d || {})));
        setDailyDocs(docs);
      } catch (err) {
        console.warn('[DailyInsights] Failed to fetch Terra daily docs:', err);
        setDailyDocs([]);
      }
    })();
  }, [wearableKey]);

  // No cross-device merge. Build per-device totals for quick tiles.
  const perDeviceTotals = useMemo(() => {
    return dailyDocs.map((d: any) => ({
      provider: (d?.provider || 'Device') as string,
      steps: ((d?.steps_data?.steps || d?.distance_data?.steps || 0) as number),
      distance: ((d?.distance_data?.distance_meters || 0) as number),
      calories: ((d?.calories_data?.total_burned_calories || 0) as number),
      oxygen: (typeof d?.oxygen_data?.avg_spo2_percent === 'number' ? d.oxygen_data.avg_spo2_percent : 0) as number,
    }));
  }, [dailyDocs]);

  // Winners for headline tiles
  const winners = useMemo(() => {
    const best = {
      steps: null as (typeof perDeviceTotals)[number] | null,
      distance: null as (typeof perDeviceTotals)[number] | null,
      calories: null as (typeof perDeviceTotals)[number] | null,
      oxygen: null as (typeof perDeviceTotals)[number] | null,
    };
    perDeviceTotals.forEach(d => {
      if (!best.steps || (d.steps || 0) > (best.steps.steps || 0)) best.steps = d;
      if (!best.distance || (d.distance || 0) > (best.distance.distance || 0)) best.distance = d;
      if (!best.calories || (d.calories || 0) > (best.calories.calories || 0)) best.calories = d;
      if (!best.oxygen || (d.oxygen || 0) > (best.oxygen.oxygen || 0)) best.oxygen = d;
    });
    return best;
  }, [perDeviceTotals]);

  return (
    <div className="w-full pt-16">
      <Header />
      <div className="max-w-3xl mx-auto p-4">
        <h2 className="text-lg font-semibold mb-4">Daily Insights</h2>
        <div className="grid grid-cols-2 gap-3 mb-6">
          {winners.steps && (
            <div className="bg-base-100 rounded-lg p-4 text-center">
              <div className="text-xs text-base-content/60">Steps · {winners.steps.provider}</div>
              <div className="text-2xl font-bold">{Math.round(winners.steps.steps)}</div>
            </div>
          )}
          {winners.distance && (
            <div className="bg-base-100 rounded-lg p-4 text-center">
              <div className="text-xs text-base-content/60">Distance · {winners.distance.provider}</div>
              <div className="text-2xl font-bold">{Math.round(winners.distance.distance)}</div>
              <div className="text-[10px] text-base-content/50">meters</div>
            </div>
          )}
          {winners.calories && (
            <div className="bg-base-100 rounded-lg p-4 text-center">
              <div className="text-xs text-base-content/60">Calories · {winners.calories.provider}</div>
              <div className="text-2xl font-bold">{Math.round(winners.calories.calories)}</div>
            </div>
          )}
          {winners.oxygen && (
            <div className="bg-base-100 rounded-lg p-4 text-center">
              <div className="text-xs text-base-content/60">Oxygen · {winners.oxygen.provider}</div>
              <div className="text-2xl font-bold">{winners.oxygen.oxygen ? Math.round(winners.oxygen.oxygen) : '-'}%</div>
            </div>
          )}
        </div>
        {(!wearableDayData || wearableDayData.length === 0) && (
          <div className="text-xs text-base-content/60 mb-4">No wearable daily data available for this day.</div>
        )}

        {dailyDocs.map((doc, idx) => (
          <div key={idx} className="bg-base-100 rounded-lg p-4 mb-4">
            <div className="text-sm font-semibold mb-2">{(doc as any)?.provider || `Device ${idx + 1}`}</div>
            {/* Oxygen saturation timeline */}
            {Array.isArray((doc as any)?.oxygen_data?.saturation_samples) && (doc as any).oxygen_data.saturation_samples.length > 0 && (
              <div className="mb-5">
                <div className="text-xs text-base-content/60 mb-1">Oxygen saturation (%)</div>
                <div style={{ height: 220 }}>
                  <ResponsiveLine
                    data={[{
                      id: 'SpO2',
                      data: (doc as any).oxygen_data.saturation_samples.map((s: any) => ({ x: new Date(s.timestamp), y: s.percentage }))
                    }]}
                    xScale={{ type: 'time', format: 'native' }}
                    xFormat="time:%H:%M"
                    yScale={{ type: 'linear', min: 80, max: 100 }}
                    axisBottom={{ tickRotation: -45, format: '%H:%M' }}
                    axisLeft={{ tickValues: 5 }}
                    colors={["#0f766e"]}
                    margin={{ top: 10, right: 10, bottom: 40, left: 40 }}
                    enablePoints={false}
                    useMesh={true}
                  />
                </div>
              </div>
            )}

            {/* Calories timeline */}
            {(() => {
              const calSamples = Array.isArray((doc as any)?.calories_data)
                ? (doc as any).calories_data
                : ((doc as any)?.calories_data?.calorie_samples || []);
              if (!Array.isArray(calSamples) || calSamples.length === 0) return null;
              return (
                <div className="mb-5">
                  <div className="text-xs text-base-content/60 mb-1">Calories (cumulative)</div>
                  <div style={{ height: 220 }}>
                    <ResponsiveLine
                      data={[{ id: 'Calories', data: calSamples.map((s: any) => ({ x: new Date(s.timestamp), y: s.calories })) }]}
                      xScale={{ type: 'time', format: 'native' }}
                      xFormat="time:%H:%M"
                      yScale={{ type: 'linear', min: 0, max: 'auto' }}
                      axisBottom={{ tickRotation: -45, format: '%H:%M' }}
                      axisLeft={{ tickValues: 5 }}
                      colors={["#065f46"]}
                      margin={{ top: 10, right: 10, bottom: 40, left: 40 }}
                      enablePoints={false}
                      useMesh={true}
                    />
                  </div>
                </div>
              );
            })()}

            {/* Heart rate timeline */}
            {Array.isArray((doc as any)?.heart_rate_data?.detailed?.hr_samples) && (doc as any).heart_rate_data.detailed.hr_samples.length > 0 && (
              <div className="mb-5">
                <div className="text-xs text-base-content/60 mb-1">Heart rate (bpm)</div>
                <div style={{ height: 220 }}>
                  <ResponsiveLine
                    data={[{ id: 'HR', data: (doc as any).heart_rate_data.detailed.hr_samples.map((s: any) => ({ x: new Date(s.timestamp), y: s.bpm })) }]}
                    xScale={{ type: 'time', format: 'native' }}
                    xFormat="time:%H:%M"
                    yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
                    axisBottom={{ tickRotation: -45, format: '%H:%M' }}
                    axisLeft={{ tickValues: 5 }}
                    colors={["#14532d"]}
                    margin={{ top: 10, right: 10, bottom: 40, left: 40 }}
                    enablePoints={false}
                    useMesh={true}
                  />
                </div>
              </div>
            )}

            {/* Distance timeline */}
            {(() => {
              const distSamples = (doc as any)?.distance_data?.detailed?.distance_samples || doc.distance_data?.distance_samples || [];
              if (!Array.isArray(distSamples) || distSamples.length === 0) return null;
              return (
                <div className="mb-5">
                  <div className="text-xs text-base-content/60 mb-1">Distance (meters)</div>
                  <div style={{ height: 220 }}>
                    <ResponsiveLine
                      data={[{ id: 'Distance', data: distSamples.map((s: any) => ({ x: new Date(s.timestamp), y: s.distance_meters })) }]}
                      xScale={{ type: 'time', format: 'native' }}
                      xFormat="time:%H:%M"
                      yScale={{ type: 'linear', min: 0, max: 'auto' }}
                      axisBottom={{ tickRotation: -45, format: '%H:%M' }}
                      axisLeft={{ tickValues: 5 }}
                      colors={["#0e7490"]}
                      margin={{ top: 10, right: 10, bottom: 40, left: 40 }}
                      enablePoints={false}
                      useMesh={true}
                    />
                  </div>
                </div>
              );
            })()}

            {/* Steps timeline */}
            {(() => {
              const stepSamples = (doc as any)?.steps_data?.step_samples || (doc as any)?.distance_data?.detailed?.step_samples || doc.distance_data?.step_samples || [];
              if (!Array.isArray(stepSamples) || stepSamples.length === 0) return null;
              return (
                <div className="mb-5">
                  <div className="text-xs text-base-content/60 mb-1">Steps</div>
                  <div style={{ height: 220 }}>
                    <ResponsiveLine
                      data={[{ id: 'Steps', data: stepSamples.map((s: any) => ({ x: new Date(s.timestamp), y: s.steps })) }]}
                      xScale={{ type: 'time', format: 'native' }}
                      xFormat="time:%H:%M"
                      yScale={{ type: 'linear', min: 0, max: 'auto' }}
                      axisBottom={{ tickRotation: -45, format: '%H:%M' }}
                      axisLeft={{ tickValues: 5 }}
                      colors={["#15803d"]}
                      margin={{ top: 10, right: 10, bottom: 40, left: 40 }}
                      enablePoints={false}
                      useMesh={true}
                    />
                  </div>
                  {/* Hourly steps bar chart */}
                  {(() => {
                    // Convert cumulative samples to hourly increments using deltas
                    const sorted = [...stepSamples]
                      .map((s: any) => ({ t: new Date(s.timestamp), v: Number(s.steps) || 0 }))
                      .sort((a, b) => a.t.getTime() - b.t.getTime());

                    const hourlyMap: Record<string, number> = {};
                    let prevVal: number | null = null;
                    sorted.forEach(({ t, v }) => {
                      let inc = 0;
                      if (prevVal !== null) {
                        inc = v - prevVal;
                        if (!Number.isFinite(inc) || inc < 0) inc = 0; // handle resets/noise
                      }
                      const h = t.getHours();
                      const label = `${h.toString().padStart(2, '0')}:00`;
                      hourlyMap[label] = (hourlyMap[label] || 0) + inc;
                      prevVal = v;
                    });
                    const hourEntries = Object.entries(hourlyMap).sort((a, b) => a[0].localeCompare(b[0]));
                    if (hourEntries.length === 0) return null;
                    const barData = hourEntries.map(([hour, steps]) => ({ hour, steps }));
                    return (
                      <div className="mt-6" style={{ height: 220 }}>
                        <div className="text-xs text-base-content/60 mb-1">Hourly steps</div>
                        <ResponsiveBar
                          data={barData}
                          keys={["steps"]}
                          indexBy="hour"
                          margin={{ top: 10, right: 10, bottom: 40, left: 40 }}
                          padding={0.3}
                          colors={["#16a34a"]}
                          axisBottom={{ tickRotation: -45 }}
                          axisLeft={{ tickValues: 5 }}
                          enableLabel={false}
                          animate={true}
                        />
                      </div>
                    );
                  })()}
                </div>
              );
            })()}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DailyInsights;


